import React, { useEffect, useMemo } from "react";
import { Button, Card, Result, Typography } from "antd";
import { useQuery } from "@tanstack/react-query";

import { fetchPublicInvoice } from "../../../api/finance/PublicInvoiceApi";
import { PublicInvoiceDto } from "../../../types/finance/PublicInvoiceTypes";
import { InvoiceDocument } from "../../../components/finance/invoicing";
import { InlineLoading } from "../../../components/shared";
import { publicInvoiceToInvoice } from "./publicInvoiceAdapter";

const { Text } = Typography;

/**
 * The public, UNAUTHENTICATED invoice pay page — I9. A patient who has no account opens the link
 * from their invoice email and lands here; the per-invoice token is the only credential.
 *
 * ── Rules this page exists to keep ────────────────────────────────────────────────────────────
 *
 *   - It NEVER reads a cookie. It fetches through `PublicInvoiceApi.fetchPublicInvoice`, which
 *     needs no session (proven in that module's test).
 *   - It renders the SHARED `InvoiceDocument` with `showInternalNotes={false}` — internal `notes`
 *     never reach a patient.
 *   - EVERY failure (no token, 404, expired/revoked, network) collapses to ONE neutral
 *     "no longer available" state — the page never distinguishes them (I6 gives an identical 404
 *     for all of them, and distinguishing here would rebuild the enumeration oracle the server
 *     avoids). Nothing here logs the token or the error object.
 *   - `Referrer-Policy: no-referrer` for this page + `rel="noreferrer"` on the Stripe link, so the
 *     token (which lives in this page's URL) is never sent to Stripe in a `Referer` header (I6).
 *   - No `<Layout>`, no nav, no widget — see the route registration and the widget suppression in
 *     `App.tsx` / `utils/invoiceLinkRouteGuard.ts`.
 */

/** Read the pay-link token from `?t=` — the emailed link shape (I7). */
function readToken(): string {
	return new URLSearchParams(window.location.search).get("t") ?? "";
}

const PublicShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<div
		data-testid="public-invoice-page"
		style={{
			minHeight: "100vh",
			background: "var(--mg-bg, #f0f2f5)",
			padding: "24px 16px",
			display: "flex",
			justifyContent: "center",
		}}
	>
		<div style={{ width: "100%", maxWidth: 820 }}>{children}</div>
	</div>
);

const UnavailableState: React.FC = () => (
	<div data-testid="public-invoice-unavailable">
		<Result
			status="404"
			title="This invoice link is no longer available"
			subTitle="Please contact the clinic for a new link."
		/>
	</div>
);

const PaySection: React.FC<{ dto: PublicInvoiceDto }> = ({ dto }) => {
	if (dto.canPayOnline && dto.stripeHostedInvoiceUrl) {
		return (
			<div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
				<Button
					type="primary"
					size="large"
					href={dto.stripeHostedInvoiceUrl}
					target="_blank"
					// ⚠️ `rel="noreferrer"` strips the Referer for THIS navigation regardless of the
					// document-level policy — the token in this page's URL must not reach Stripe (I6).
					rel="noreferrer"
					referrerPolicy="no-referrer"
					data-testid="public-invoice-pay"
				>
					Pay online
				</Button>
			</div>
		);
	}

	if (dto.isPaidInFull) {
		return (
			<div style={{ marginTop: 16 }} data-testid="public-invoice-paid">
				<Text strong style={{ color: "var(--mg-success)" }}>
					Paid in full — thank you.
				</Text>
			</div>
		);
	}

	// PAYLINK/PAYID: non-Stripe, unpaid. The methods are NON-EXCLUSIVE — every selected+configured
	// surface renders. Precedence for the PRIMARY action: Stripe (above) > paid-in-full (above) >
	// the local methods here. The "Pay now" button mirrors the payment link that `InvoiceDocument`
	// also lists; the bank details and the PayID render inside `InvoiceDocument`. The EFT prompt is a
	// true fallback — shown ONLY when no local surface is present at all.
	//
	// ⚠️ Re-check the link scheme even though the server already withholds a non-http(s) link: this
	// value becomes an `href`, so a `javascript:`/other scheme must never be clickable. Belt-and-
	// braces with `PublicInvoiceService.isHttpUrl`; a bad value simply yields no "Pay now" button.
	const methods = dto.localPaymentMethods ?? ["bank_transfer"];
	const hasSafeLink =
		methods.includes("payment_link") &&
		!!dto.paymentLink &&
		/^https?:\/\//i.test(dto.paymentLink);
	const hasBank =
		methods.includes("bank_transfer") &&
		!!(dto.clinic.bankAccountName || dto.clinic.bankBsb || dto.clinic.bankAccountNumber);
	const hasPayId = methods.includes("payid") && !!dto.payId;
	const anyLocalSurface = hasSafeLink || hasBank || hasPayId;

	return (
		<>
			{hasSafeLink && (
				<div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
					<Button
						type="primary"
						size="large"
						href={dto.paymentLink as string}
						target="_blank"
						// ⚠️ `rel="noreferrer"` strips the Referer for THIS navigation regardless of the
						// document-level policy — the token in this page's URL must not reach the link host.
						rel="noreferrer"
						referrerPolicy="no-referrer"
						data-testid="public-invoice-paylink"
					>
						Pay now
					</Button>
				</div>
			)}

			{/*
			  The bank / EFT details and the PayID render inside `InvoiceDocument` above. Only when NO
			  local surface is present at all do we fall back to a bare EFT prompt.
			*/}
			{!anyLocalSurface && (
				<div style={{ marginTop: 16 }} data-testid="public-invoice-eft">
					<Text type="secondary">
						Please pay by EFT using the payment details above, quoting your invoice number.
					</Text>
				</div>
			)}
		</>
	);
};

const PublicInvoicePage: React.FC = () => {
	const token = useMemo(readToken, []);

	// Referrer-Policy: no-referrer for THIS page. The app-wide policy (index.html) is
	// `strict-origin-when-cross-origin`; there is no per-route <head> mechanism (no react-helmet),
	// so we override by injecting a referrer meta on mount and removing it on unmount. The Stripe
	// link additionally carries `rel="noreferrer"` as the definitive per-navigation guard.
	useEffect(() => {
		const meta = document.createElement("meta");
		meta.name = "referrer";
		meta.content = "no-referrer";
		document.head.appendChild(meta);
		return () => {
			document.head.removeChild(meta);
		};
	}, []);

	const { data, isLoading, isError } = useQuery({
		queryKey: ["public-invoice", token],
		queryFn: () => fetchPublicInvoice(token),
		enabled: token !== "",
		// A 404 is the final answer for a bad/expired/revoked token — do not retry it.
		retry: false,
		// The token is a bearer credential — do not keep the fetched invoice cached beyond a view.
		gcTime: 0,
	});

	if (token === "" || isError) {
		return (
			<PublicShell>
				<UnavailableState />
			</PublicShell>
		);
	}

	if (isLoading || !data) {
		return (
			<PublicShell>
				<InlineLoading message="Loading invoice…" minHeight={240} />
			</PublicShell>
		);
	}

	const { invoice, settings } = publicInvoiceToInvoice(data);

	return (
		<PublicShell>
			<Card>
				<InvoiceDocument
					invoice={invoice}
					settings={settings}
					patientName={data.patientName ?? ""}
					showInternalNotes={false}
				/>
				<PaySection dto={data} />
			</Card>
		</PublicShell>
	);
};

export default PublicInvoicePage;
